import React, { useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';

import axiosInstance from 'src/utils/axios';
import { GenericErrorPage } from 'src/pages/GenericErrorPage';
import { getRequestInterceptor, getResponseInterceptor } from 'src/libraries/axios.interceptors';

//
//

// Props type for the error boundary
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

//

const AxiosInterceptors: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const requestInterceptor = getRequestInterceptor();
    const responseInterceptor = getResponseInterceptor();

    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [enqueueSnackbar]);

  return null;
};

//

export const SentryErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const handleError = useCallback((error: Error, errorInfo: any) => {
    console.error('Sentry Error Boundary:', error, errorInfo);
    Sentry.captureException(error, { extra: errorInfo });
    // Commented this since we are using the generic error page 🤔
    // enqueueSnackbar('Unfortunately, an error has occurred.', {
    //   variant: 'error',
    // });
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={GenericErrorPage} onError={handleError}>
      <AxiosInterceptors />
      {children}
    </Sentry.ErrorBoundary>
  );
};
